import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { createAdminApiClient, AdminApiError } from '@/lib/admin-api-client';
import { createUserSchema } from '@/lib/admin-validation';
import type { UserRole } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function CreateUserPage() {
  const { idToken } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const client = createAdminApiClient(
    import.meta.env.VITE_API_GATEWAY_BASE_URL,
    () => idToken ?? '',
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setApiError(null);

    const result = createUserSchema.safeParse({ email, temporaryPassword, role });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const field = String(issue.path[0]);
        if (!fieldErrors[field]) fieldErrors[field] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await client.createUser({ email, temporaryPassword, role, ...(phoneNumber && { phoneNumber }) });
      setSuccess(true);
      setTimeout(() => navigate('/admin'), 1500);
    } catch (err) {
      if (err instanceof AdminApiError) {
        setApiError(err.errorMessage);
        console.error('[CreateUserPage] POST /users', err.status, err.errorMessage);
      } else {
        setApiError('An unexpected error occurred');
        console.error('[CreateUserPage] POST /users', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section aria-label="Create User">
      <h2 className="mb-6 text-2xl font-semibold">Create User</h2>

      {success && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400" role="status">
          User created successfully. Redirecting...
        </div>
      )}

      {apiError && (
        <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-destructive" role="alert">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="max-w-md space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium">
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!errors.email}
            disabled={isSubmitting}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-destructive">{errors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="phoneNumber" className="mb-1 block text-sm font-medium">
            Phone Number <span className="text-muted-foreground">(optional)</span>
          </label>
          <Input
            id="phoneNumber"
            type="tel"
            placeholder="+12125551234"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={isSubmitting}
          />
          <p className="mt-1 text-xs text-muted-foreground">E.164 format: +country code and number, no spaces (e.g. +12125551234)</p>
        </div>

        <div>
          <label htmlFor="temporaryPassword" className="mb-1 block text-sm font-medium">
            Temporary Password
          </label>
          <Input
            id="temporaryPassword"
            type="password"
            value={temporaryPassword}
            onChange={(e) => setTemporaryPassword(e.target.value)}
            aria-invalid={!!errors.temporaryPassword}
            disabled={isSubmitting}
          />
          {errors.temporaryPassword && (
            <p className="mt-1 text-sm text-destructive">
              {errors.temporaryPassword}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="role" className="mb-1 block text-sm font-medium">
            Role
          </label>
          <Select
            value={role}
            onValueChange={(val) => setRole(val as UserRole)}
            disabled={isSubmitting}
          >
            <SelectTrigger aria-label="Role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="read-only">Read-only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create User'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
